import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { sanityClient, imgSrc } from "@/lib/sanity";
import { allContentQuery } from "@/lib/queries";
import * as fallback from "@/lib/site";

const ContentContext = createContext(null);

// ---- Sanity → static-shape adapters -------------------------------------------------
const toDoctor = (d) => ({
  id: d._id,
  name: d.name,
  slug: d.slug || (d.name || "").toLowerCase().replace(/[^a-z0-9]+/g, "-"),
  role: d.role || "",
  image: imgSrc(d.image, 1200) || fallback.DOCTOR.image,
  bio: d.bio || "",
  quote: d.quote || "",
  expertise: Array.isArray(d.expertise) ? d.expertise : [],
  stats: Array.isArray(d.stats) ? d.stats.map((s) => ({ value: s.value, label: s.label })) : [],
  featured: !!d.featured,
});

const toService = (s, i) => ({
  key: s.key || `service-${i}`,
  title: s.title || "",
  items: Array.isArray(s.items) ? s.items : [],
  image: imgSrc(s.image, 1200) || fallback.SERVICES[i % fallback.SERVICES.length].image,
});

const toPost = (p) => ({
  id: p._id,
  slug: p.slug,
  title: p.title || "",
  excerpt: p.excerpt || "",
  tag: p.tag || "Article",
  date: p.publishedAt ? new Date(p.publishedAt).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "2-digit" }) : "",
  read: p.readTime || "5 min read",
  image: imgSrc(p.image, 1400) || fallback.BLOGS[0].image,
  body: p.body || null, // portable text array, rendered by PortableText
});

const toGallery = (g) => ({ url: imgSrc(g.image, 1400), cap: g.caption || "" });

const toTestimonial = (t) => ({ name: t.name, place: t.place || "", stars: t.stars || 5, text: t.text || "" });

const toAward = (a) => ({ title: a.title, text: a.text || "" });

const toFallback = () => ({
  hero: null,
  siteConfig: { ...fallback.CLINIC },
  doctors: [
    { id: "static", name: fallback.DOCTOR.name, slug: "pramod-kumar-karan", role: fallback.DOCTOR.role, image: fallback.DOCTOR.image, bio: fallback.DOCTOR.bio, quote: fallback.DOCTOR.quote, expertise: fallback.DOCTOR.expertise, stats: fallback.DOCTOR.stats, featured: true },
  ],
  services: fallback.SERVICES,
  posts: fallback.BLOGS.map((b) => ({ ...b, id: b.slug })),
  gallery: fallback.GALLERY,
  testimonials: fallback.TESTIMONIALS,
  awards: fallback.AWARDS,
  features: fallback.FEATURES,
  mission: fallback.MISSION,
  why: fallback.WHY.map((w) => ({ ...w, id: w.title })),
});

// merge: prefer Sanity when non-empty
const nonEmpty = (a) => Array.isArray(a) && a.length > 0;

export function ContentProvider({ children }) {
  const [state, setState] = useState({ ...toFallback(), loading: true, source: "static" });

  useEffect(() => {
    let cancelled = false;
    if (!sanityClient) {
      setState((s) => ({ ...s, loading: false }));
      return;
    }
    sanityClient
      .fetch(allContentQuery)
      .then((r) => {
        if (cancelled) return;
        const base = toFallback();
        const sc = r?.siteConfig || {};
        setState({
          loading: false,
          source: r ? "sanity" : "static",
          hero: r?.hero || null,
          siteConfig: {
            ...base.siteConfig,
            ...(sc.phone && { phone: sc.phone }),
            ...(sc.phoneRaw && { phoneRaw: sc.phoneRaw }),
            ...(sc.email && { email: sc.email }),
            ...(sc.whatsapp && { whatsapp: sc.whatsapp }),
            ...(sc.address && { address: sc.address }),
            ...(sc.hours && { hours: sc.hours }),
            ...(sc.tagline && { tagline: sc.tagline }),
            ...(sc.since && { since: sc.since }),
            ...(sc.facebook && { facebook: sc.facebook }),
            ...(sc.instagram && { instagram: sc.instagram }),
            logo: imgSrc(sc.logo, 400) || null,
          },
          doctors: nonEmpty(r?.doctors) ? r.doctors.map(toDoctor) : base.doctors,
          services: nonEmpty(r?.services) ? r.services.map(toService) : base.services,
          posts: nonEmpty(r?.posts) ? r.posts.map(toPost) : base.posts,
          gallery: nonEmpty(r?.gallery) ? r.gallery.map(toGallery).filter((g) => g.url) : base.gallery,
          testimonials: nonEmpty(r?.testimonials) ? r.testimonials.map(toTestimonial) : base.testimonials,
          awards: nonEmpty(r?.awards) ? r.awards.map(toAward) : base.awards,
          features: nonEmpty(r?.features) ? r.features : base.features,
          mission: nonEmpty(r?.mission) ? r.mission : base.mission,
          why: nonEmpty(r?.why) ? r.why : base.why,
        });
      })
      .catch((err) => {
        console.warn("[Sanity] fetch failed — using static fallback content", err?.message);
        if (!cancelled) setState((s) => ({ ...s, loading: false, source: "static" }));
      });
    return () => { cancelled = true; };
  }, []);

  const value = useMemo(() => state, [state]);
  return <ContentContext.Provider value={value}>{children}</ContentContext.Provider>;
}

export function useContent() {
  const ctx = useContext(ContentContext);
  if (!ctx) throw new Error("useContent must be used within ContentProvider");
  return ctx;
}

// helpers
export const useDoctor = (slug) => {
  const { doctors } = useContent();
  return doctors.find((d) => d.slug === slug) || doctors[0];
};
export const useFeaturedDoctor = () => {
  const { doctors } = useContent();
  return doctors.find((d) => d.featured) || doctors[0];
};
export const usePost = (slug) => {
  const { posts } = useContent();
  return posts.find((p) => p.slug === slug);
};

export const buildWa = (whatsapp, message) =>
  `https://wa.me/${whatsapp}?text=${encodeURIComponent(message || "Hello ALCH, I would like to book an appointment.")}`;
